import Token from './auth-token-wrapper'
import api from './api'

// Flag to prevent multiple concurrent refresh attempts
let isRefreshing = false
// Queue of failed requests to retry after token refresh
let failedRequestsQueue = []

/**
 * Process the queue of failed requests by retrying them with the new token
 */
const processQueue = (error = null) => {
  failedRequestsQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve()
    }
  })

  // Reset the queue
  failedRequestsQueue = []
}

/**
 * Setup response interceptor for an axios instance
 * @param {AxiosInstance} instance - The axios instance to add the interceptor to
 */
export const setupResponseInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      // If the error is not 401/500-with-401-message or the request already tried to refresh, just reject
      const is401Error =
        error.response?.status === 401 ||
        (error.response?.status === 500 &&
          error.response?.data?.message?.includes('401 Unauthorized'))

      if (!error.response || !is401Error || originalRequest._retry) {
        return Promise.reject(error)
      }

      // Mark this request as having already attempted a retry
      originalRequest._retry = true

      // If we're already refreshing, add this request to the queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject })
        })
          .then(() => {
            // Retry with new token
            originalRequest.headers.authorization = `Bearer ${Token.getToken()}`
            return instance(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      // Start refreshing
      isRefreshing = true

      try {
        // Check if we still have a refresh token before attempting refresh
        // This prevents errors after logout
        const refreshToken = Token.getRefreshToken()
        if (!refreshToken) {
          // No refresh token available (user might have logged out)
          processQueue(new Error('No refresh token available'))
          return Promise.reject(error)
        }

        // Attempt to refresh the token
        const response = await api.auth.refreshToken()
        Token.setAuthTokens(response.data)

        // Update authorization header
        originalRequest.headers.authorization = `Bearer ${Token.getToken()}`

        // Process all queued requests
        processQueue()

        // Retry the original request
        return instance(originalRequest)
      } catch (refreshError) {
        // Token refresh failed
        processQueue(refreshError)
        // Preserve SSO cookie to allow auto-login handoff
        Token.removeLocalStorageTokensOnly()

        // If an SSO cookie is present, skip reload to avoid infinite redirect loops.
        // Component-level .catch() handlers will deal with the error gracefully.
        if (!Token.getSSoToken()) {
          window.location.reload()
        }

        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }
  )
}
